import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';

import { ApWebContainerService, NativeAppType, NativeProcessStatus, NativeRequestMessage, NativeSign } from '@ap/web-container';

import { WEBPART_MESSAGING_IS_MOBILE, WebpartRequestMessage } from './webpart-messaging.model';
import { AnalyticsService } from './analytics/analytics.service';

export function dispatchWebpartMessage(identifier, type, payload = null) {
    const event = new CustomEvent('dispatch-webpart-message', {
        detail: { identifier, type, payload },
        bubbles: true,
        composed: true
    });
    window.dispatchEvent(event);
}

// Make available for non-module components (e.g. PHP or vanilla JS)
(window as any).dispatchWebpartMessage = dispatchWebpartMessage;

@Injectable({
    providedIn: 'root'
})
export class WebpartMessagingService {
    private readonly apWebContainerService: ApWebContainerService = inject(ApWebContainerService);
    private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
    private readonly router = inject(Router, { optional: true });
    private readonly activatedRoute = inject(ActivatedRoute, { optional: true });

    protected isMobile = inject(WEBPART_MESSAGING_IS_MOBILE);

    constructor() {
        // When not in mobile-standalone mode, detect isMobile via router data (mijn-omgeving web context)
        if (!this.isMobile && this.router) {
            this.router.events
                .pipe(
                    filter((event) => event instanceof NavigationEnd),
                    map(() => {
                        let route = this.router.routerState.root;
                        while (route.firstChild) {
                            route = route.firstChild;
                        }
                        return route;
                    })
                )
                .subscribe((route) => {
                    this.isMobile = !!route.snapshot?.data?.isMobile;
                });
        }
    }

    /* eslint-disable */
    onTransportEvent(event: CustomEvent) {
        const { identifier, type, payload } = event.detail;
        switch (type) {
            /*
             * setTitle
             * @description
             * trigger to display a title in header
             * @param payload the title to display
             */
            case NativeRequestMessage.SET_TITLE:
                if (this.isMobile) {
                    this.apWebContainerService.setTitle(payload);
                } else {
                    this.onSetTitleWeb(identifier, payload);
                }
                break;
            /*
             * @description
             * triggers the native app to display navigation buttons on top of the page
             * not needed for Web
             * @param payload: true/false
             */
            case NativeRequestMessage.SET_NAVIGATION: {
                if (this.isMobile) {
                    this.apWebContainerService.setNavigation({ back: payload });
                }
                break;
            }
            /*
             * @description
             * signal coming from the native app to navigate back in the process
             */
            case NativeRequestMessage.NAVIGATE_BACK:
                if (this.isMobile) {
                    this.apWebContainerService.onNavigateBack().subscribe(() => {
                        this.apWebContainerService.finishProcess(NativeProcessStatus.SUCCESS);
                    });
                } else {
                    window.history.back();
                }
                break;
            /*
             * @description
             * communicates the end of the process to the native app
             * @param payload the process end status
             */
            case NativeRequestMessage.FINISH_PROCESS:
                if (this.isMobile) {
                    this.apWebContainerService.finishProcess(payload);
                } else {
                    window.history.back();
                }
                break;
            /*
             * @description
             * requests the native app to completely handle the signing of the authenticationRequest
             *
             * @param actionId the actionId of the authenticationRequest
             * @returns an observable with the result from signing in the native app
             */
            case NativeRequestMessage.SIGN_NATIVE:
                if (this.isMobile) {
                    this.apWebContainerService.sign(payload.actionId).subscribe((nativeSign: NativeSign) => {
                        if (payload?.response && typeof payload.response === 'function') {
                            payload.response(nativeSign);
                        }
                    });
                } else {
                    this.onSignNativeWeb(identifier, payload);
                }
                break;
            /*
             * @description
             * communicates downloadable data to the native app
             * @param blob: JS Blob object,
             *        filename: string
             */
            case NativeRequestMessage.DOWNLOAD:
                if (this.isMobile) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const base64data = reader.result.toString();
                        this.apWebContainerService.downloadFile(
                            base64data.substring(base64data.indexOf(',') + 1),
                            payload.blob.type,
                            payload.filename
                        );
                    };
                    reader.readAsDataURL(payload.blob);
                } else {
                    this.onDownloadWeb(identifier, payload);
                }
                break;
            /*
             * @description
             * mobile: returns the NativeSelectedAccount that was selected in the native app before entering the webcontainer
             * web: returns the NativeSelectedAccount that was stored in session before entering the webcontainer
             *
             * @returns an observable containing the NativeSelectedAccount object
             */
            case NativeRequestMessage.GET_SELECTED_ACCOUNT:
                if (this.isMobile) {
                    this.apWebContainerService.getSelectedAccount().subscribe((selectedAccount) => {
                        if (payload?.response && typeof payload.response === 'function') {
                            payload.response(selectedAccount);
                        }
                    });
                } else {
                    this.onGetSelectedAccountWeb(identifier, payload);
                }
                break;
            /*
             * @description  !! used for setting user in mijn-kantoor!!!
             * communicates the active user to the native app
             */
            case NativeRequestMessage.SET_USER:
                alert(`${type} not implemented for mobiel/web`);
                break;
            /*
             * @description
             * returns the native application type
             *
             * @returns an observable containing the native application type
             */
            case NativeRequestMessage.GET_APP_TYPE:
                if (this.isMobile) {
                    this.apWebContainerService.getAppType().subscribe((appType) => {
                        if (payload?.response && typeof payload.response === 'function') {
                            payload.response(appType);
                        }
                    });
                } else {
                    if (payload?.response && typeof payload.response === 'function') {
                        payload.response(NativeAppType.BANKING);
                    }
                }
                break;
            /*
             * @description
             * triggers the native app to start the ReadId identification process
             */
            case NativeRequestMessage.START_IDENTIFICATION:
                if (this.isMobile) {
                    this.apWebContainerService.startIdentification(
                        payload.idType,
                        payload.flowType,
                        payload.submitterIdentifier,
                        payload.attemptsRequiredToSkipNFC,
                        payload.shouldRequirePersonalNumber
                    );
                } else {
                    alert(`${type} only available for mobile`);
                }
                break;
            /*
             * @description
             * triggers the native app to close the webcontainer, start the native token registration process
             * and return to the returnUrl in a new webcontainer
             */
            case NativeRequestMessage.START_TOKEN_REGISTRATION:
                if (this.isMobile) {
                    this.apWebContainerService.startTokenRegistration(payload.returnUrl, payload.isExistingCustomer, payload.pairingCode);
                } else {
                    alert(`${type} only available for mobile`);
                }
                break;
            /*
             * @description
             * a request to check if the device that the app is running on has nfc capabilities
             *
             * @returns an observable containing a boolean if the current device is nfc capable
             */
            case NativeRequestMessage.GET_NFC_SUPPORT:
                if (this.isMobile) {
                    this.apWebContainerService.isNfcCapable().subscribe((nfcCapable) => {
                        if (payload?.response && typeof payload.response === 'function') {
                            payload.response(nfcCapable.result);
                        }
                    });
                } else {
                    if (payload?.response && typeof payload.response === 'function') {
                        payload.response(false);
                    }
                }
                break;
            /*
             * @description
             * mobile: returns the NativeSelectedDebitCard that was selected in the native app before entering the webcontainer
             * web: returns the NativeSelectedDebitCard that was stored in session before entering the webcontainer
             *
             * @returns an observable containing the selected debit card
             */
            case NativeRequestMessage.GET_SELECTED_DEBIT_CARD:
                if (this.isMobile) {
                    this.apWebContainerService.getSelectedDebitCard().subscribe((result) => {
                        if (payload?.response && typeof payload.response === 'function') {
                            payload.response(result);
                        }
                    });
                } else {
                    this.onGetSelectedDebitCardWeb(identifier, payload);
                }
                break;
            /*
             * @description
             * returns the identification that was selected in the native app before entering the webcontainer
             *
             * @returns an observable with one of the enumeration values
             */
            case NativeRequestMessage.GET_SELECTED_IDENTIFICATION:
                if (this.isMobile) {
                    this.apWebContainerService.getSelectedIdentification().subscribe((result) => {
                        if (payload?.response && typeof payload.response === 'function') {
                            payload.response(result);
                        }
                    });
                } else {
                    this.onGetSelectedIdentificationWeb(identifier, payload);
                }
                break;
            /*
             * @description
             * triggers the native app to end the current session and return to the login screen
             */
            case NativeRequestMessage.LOG_OUT:
                if (this.isMobile) {
                    this.apWebContainerService.logOut();
                } else {
                    this.onLogOutWeb(identifier, payload);
                }
                break;
            case WebpartRequestMessage.PUSH_ANALYTICS_EVENT:
                if(payload.type === 'click'){
                    this.analyticsService.pushAnalyticsClickEvent(payload.element, payload.action);
                } else if(payload.type === 'pageview'){
                    this.analyticsService.pushAnalyticsPageview(payload.page, payload.isBusiness);
                } else {
                    this.analyticsService.pushAnalyticsFormStep(payload.stepName, payload.stepNumber, payload.status, payload.fieldValues);
                }
                break;
            case WebpartRequestMessage.IS_MOBILE_CONTAINER:
                if (payload?.response && typeof payload.response === 'function') {
                    payload.response(this.isMobile);
                }
                break;
            case WebpartRequestMessage.GIVE_PARAMETERS:
                if (payload?.response && typeof payload.response === 'function') {
                    payload.response(this.getQueryParams(identifier));
                }
                break;
            default:
                console.error('ERROR WebpartMessagingService: ', type, !!payload ? payload : '', 'not implemented'); // NOSONAR
        }
    }

    public onClose() {
        window.history.back();
    }

    // Hooks for mijn-omgeving to override web-specific behaviour by extending this service
    protected onSetTitleWeb(identifier: string, payload: string): void {}
    protected onSignNativeWeb(identifier: string, payload: any): void {}
    protected onDownloadWeb(identifier: string, payload: any): void {}
    protected onGetSelectedAccountWeb(identifier: string, payload: any): void {}
    protected onGetSelectedDebitCardWeb(identifier: string, payload: any): void {}
    protected onGetSelectedIdentificationWeb(identifier: string, payload: any): void {}
    protected onLogOutWeb(identifier: string, payload: any): void {}

    protected getQueryParams(identifier: string): Record<string, string> {
        if (!this.activatedRoute) {
            return {};
        }

        const queryStringParams = this.activatedRoute.snapshot.queryParams;
        if (queryStringParams && Object.keys(queryStringParams).length > 0) {
            return queryStringParams;
        }

        return {};
    }
}
